import { useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../../api/queryClient";
import AppContext from "../../../../AppContext";
import { APP_TITLE } from "../../../../config/constant";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import FormContainer from "../../../common/forms/FormContainer";
import FormField from "../../../common/forms/FormField";
import ListComponent, { StatusBadge } from "../../../common/lists/ListComponent";
import ContainerLoader from "../../../common/loaders/ContainerLoader";
import MainWindowHeader, { CustomSearch } from "../../../common/mainApp/mainWindowHeaders/MainWindowHeader";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.my-defaults-sizes-page {
        display: flex;
        flex-direction: column;
    }

    .add-size-row {
        display: flex;
        align-items: flex-end;
        gap: 16px;
        width: 100%;
    }

    .add-size-system-name {
        flex: 0 0 254px;
        min-width: 0;
    }

    .add-size-input {
        flex: 1 1 auto;
        min-width: 320px;
    }

    .add-size-button {
        width: 64px;
        height: 37px;
        flex-shrink: 0;
    }

    .table-action-icon {
        width: 16px;
        height: 16px;
        color: var(--text-icons-gray-100);
    }

    @media (max-width: 900px) {
        .add-size-row {
            flex-wrap: wrap;
        }

        .add-size-system-name,
        .add-size-input {
            flex-basis: 100%;
        }
    }

    .rdt_Pagination {
        display: none !important;
    }

    .table-structure > div:nth-child(2) {
        height: 24px;
    }
`;
// #endregion

export default function MyDefaultSizesPage() {
    document.title = `${APP_TITLE} - My Default Sizes`;

    // #region VARS
    const { isLoadingApp } = useContext(AppContext);
    const { MainLayoutProps, layoutType } = useOutletContext();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region STATES
    //----- Header Filters -----
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [allPOSList, setAllPOSList] = useState([]);
    const [selectedRetailBrand, setSelectedRetailBrand] = useState(null);
    const [filteredPOS, setFilteredPOS] = useState(undefined);
    const [requestsEnabled, setRequestsEnabled] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [pageWidth, setPageWidth] = useState(window.innerWidth);

    const filters = {
        filterRetailBrandIDs: selectedRetailBrand ? [selectedRetailBrand?.value] : [],
        filterPOSIDs: filteredPOS?.map(item => item?.value)
        //searchText: searchText
    };

    // #endregion

    // #region LIST COMPONENT VARS
    const [listComponentIsLoading, setListComponentIsLoading] = useState(false);
    const [listNumRows, setListNumRows] = useState(50);
    const listQueryKey = ["MyDefaultSizesList", filters];
    const listColumns = [
        {
            name: "Size system",
            selector: row => row?.sizeSystem ?? "--",
            width: "12%"
        },
        {
            name: "Size system ID",
            selector: row => row?.sizeSystemID || "--",
            width: "8%"
        },
        {
            name: "Sizes",
            selector: row => (
                <div className="text-one-line" style={{ maxWidth: pageWidth < 2400 ? "1000px" : "1500px" }}>
                    {row?.sizes || "--"}
                </div>
            ),
            tooltipContent: row => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {row?.sizes || "--"}
                </div>
            ),
            tooltipOverlayStyle: { maxWidth: "600px" }
        },
        {
            name: "Status",
            selector: row => <StatusBadge statusValue={row?.status ?? "--"} colorValue={"#00bb00"} />,
            maxWidth: "200px"
        }
    ];

    const mapListData = data => {
        /* 
        // USING HARDCODED DATA while this draft still points to getStoresData.
        const SIZE_SYSTEMS_TEST_DATA = [
            {
                _id: "size-system-307",
                sizeSystem: "SML",
                posSizeSystemID: "307",
                sizes: "XXXS, XXS, XS, (+11)",
                sizeCount: "11",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-308",
                sizeSystem: "Belter",
                posSizeSystemID: "308",
                sizes: "70, 75, 80, (+7)",
                sizeCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-310",
                sizeSystem: "Fransk",
                posSizeSystemID: "310",
                sizes: "0, 1, 2, (+7)",
                sizeCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-312",
                sizeSystem: "Dame US",
                posSizeSystemID: "312",
                sizes: "4, 8, 12, P, 2, 6, 10, 14",
                sizeCount: "8",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-313",
                sizeSystem: "Dame GB",
                posSizeSystemID: "313",
                sizes: "6, 10, 14, 2, 4, 8, 12 (+3)",
                sizeCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-314",
                sizeSystem: "Dame",
                posSizeSystemID: "314",
                sizes: "32, 34, 36, 38 (+6)",
                sizeCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-315",
                sizeSystem: "Dame italiensk",
                posSizeSystemID: "315",
                sizes: "XXXS, XXS, XS, (+11)",
                sizeCount: "8",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-317",
                sizeSystem: "Herre full",
                posSizeSystemID: "317",
                sizes: "62, 82, 58, (+37)",
                sizeCount: "39",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "size-system-318",
                sizeSystem: "Tommer",
                posSizeSystemID: "318",
                sizes: "28, 29, 30, 31, (+13)",
                sizeCount: "17",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            }
        ];*/

        const testData = data?.map(item => {
            return {
                sizeSystem: item?.sizeGroupName,
                sizeSystemID: item?.sizeGroupID,
                sizes: item?.sizeNames?.join(", "),
                status: item?.status ?? "Synced"
            };
        });

        return testData;
    };
    // #endregion

    // #region METHODS
    const onSearch = value => {
        setSearchValue(value || "");
    };

    const onRefreshAction = () => {
        AppQueryClient.invalidateQueries(listQueryKey);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrandValue =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrandValue
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrandValue) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setSelectedRetailBrand(defaultRetailBrandObj);
        }

        if (data?.posSystems?.length) {
            const formFields = data?.posSystems?.map(item => ({
                ...item,
                name: item.name,
                value: item._id,
                selected: true
            }));

            setAllPOSList(formFields);
            setFilteredPOS(formFields);
        }

        setRequestsEnabled(true);
    };

    const onRetailBrandChange = selection => {
        setSelectedRetailBrand(selection);
        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onSelectedPOSChanged = (selections, dropdownDataWithSelections) => {
        setFilteredPOS(selections);
        setAllPOSList(dropdownDataWithSelections);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            searchValue,
            onSearch,
            onRefreshAction,
            allRetailBrandsList,
            onRetailBrandChange,
            selectedRetailBrand,
            allPOSList,
            onSelectedPOSChanged,
            onFiltersLoaded
        });
    }, [allRetailBrandsList, selectedRetailBrand, allPOSList]);

    useEffect(() => {
        const handleResize = () => {
            setPageWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // #endregion

    // #region RENDER
    return (
        <StyledPage className="my-defaults-sizes-page">
            <FormContainer
                title={
                    <div>
                        DEFAULT SIZES DETAILS - <span className="text-orange-500">NOT POSSIBLE WITH FRONT SYSTEMS</span>
                    </div>
                }
                showDefaultRightLegends={true}>
                <div className="add-size-row">
                    <div className="add-size-system-name">
                        <FormField
                            type="text"
                            title="Size system name"
                            placeholder="Insert size system name"
                            requiredType="disabledSimple"
                            varName="sizeSystemName"
                            tooltip="Size system name"
                        />
                    </div>

                    <div className="add-size-input">
                        <FormField
                            type="text"
                            title="Size input"
                            placeholder="Insert sizes + ENTER"
                            requiredType="disabledSimple"
                            varName="sizeInput"
                            tooltip="Size input"
                        />
                    </div>

                    <BtnLargeSolid
                        className="add-size-button"
                        text="ADD"
                        disabled={true}
                        normalColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverBorderColor="var(--text-icons-orange-500)"
                        disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                        textStyle={{
                            fontSize: "11px",
                            textTransform: "uppercase"
                        }}
                    />
                </div>
            </FormContainer>

            <ContainerLoader
                isLoading={!isLoadingApp && listComponentIsLoading}
                className="w-100"
                isLoadingOverlay={true}
                addBlurFilters={false}>
                <ListComponent
                    className="w-100"
                    tableClassName="expandedRowsCentered"
                    title="DEFAULT SIZES - AS IN POS"
                    columns={listColumns}
                    api={{
                        useBOstageXAPI: true,
                        BOstageXAPIEndpoint: "X/Products/getProductSizeGroupsFromPOS",
                        formData: {
                            retailBrandID: selectedRetailBrand?.value || "",
                            limit: listNumRows,
                            ...filters
                        },
                        onSuccess: mapListData
                    }}
                    selectableRows={true}
                    showLoader={false}
                    onListLoading={loading => setListComponentIsLoading(loading)}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    requestEnabled={requestsEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    actions={{
                        includeActions: true,
                        editIsVisible: true,
                        deleteIsVisible: true,
                        suspendIsVisible: false,
                        actionColumnWidth: "100px",
                        actionColumnTitle: "Actions"
                    }}
                />
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders({
    props,
    searchValue,
    onSearch,
    onRefreshAction,
    allRetailBrandsList,
    onRetailBrandChange,
    selectedRetailBrand,
    allPOSList,
    onSelectedPOSChanged,
    onFiltersLoaded
}) {
    props.setControlBarLeftBtns([
        {
            customSearch: (
                <CustomSearch
                    enabled={false}
                    placeholder="Search"
                    onTextChange={onSearch}
                    searchValue={searchValue}
                    onClearSearch={() => onSearch("")}
                />
            )
        }
    ]);

    props.setControlBarCenterBtns([
        { to: "/products", active: false, disabled: false, title: "LIVE" },
        { to: "/products/my-defaults/seasons", active: true, disabled: false, title: "MY DEFAULTS" }
    ]);

    props.setControlBarRightBtns([
        {
            to: "/products/my-defaults/seasons",
            active: false,
            disabled: false,
            title: "Seasons",
            icon: "snowflake-02"
        },
        {
            to: "/products/my-defaults/genders",
            active: false,
            disabled: false,
            title: "Genders",
            icon: "link-05"
        },
        {
            to: "/products/my-defaults/colors",
            active: false,
            disabled: false,
            title: "Colors",
            icon: "palette"
        },
        {
            to: "/products/my-defaults/categories",
            active: false,
            disabled: false,
            title: "Categ",
            icon: "grid-01"
        },
        {
            to: "/products/my-defaults/sizes",
            active: true,
            disabled: false,
            title: "Sizes",
            icon: "ruler"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "SAVE",
                        enabled: false,
                        onClick: undefined
                    },
                    gridEnabled: false,
                    listEnabled: false
                }}
                search={{
                    visible: false
                }}
                onRefreshClick={onRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allRetailBrandsList,
                    onDropdownChange: onRetailBrandChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: selectedRetailBrand?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onSelectedPOSChanged,
                    disabled: false,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all POS",
                        labelAllSelected: "All POS",
                        labelNoneSelected: "--",
                        allItems: allPOSList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        usePOSSystems: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                settingsButton={{
                    visible: true,
                    enabled: false
                }}
            />
        ),
        visible: false,
        gridEnabled: false,
        listEnabled: false,
        onRefreshAction
    });
}
// #endregion

// #region SMALL COMPONENTS

// #endregion
